import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicEntranceExam() {
  const [token, setToken] = useState('');
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState('welcome'); // welcome, info, playing, finished
  const [studentName, setStudentName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [stars, setStars] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);

  // Extract token from URL
  useEffect(() => {
    const path = window.location.pathname;
    const t = path.split('/admission-exam/')[1];
    if (t) {
      setToken(t);
      loadExam(t);
    } else {
      setError('Invalid exam link');
      setLoading(false);
    }
  }, []);

  const loadExam = async (t) => {
    try {
      const res = await fetch(`${API_URL}/api/admission/exam/take/${t}`);
      const data = await res.json();
      if (data.success) {
        setExam(data);
        setTimeLeft(data.time_limit_minutes * 60);
      } else {
        setError(data.detail || 'Exam not found');
      }
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  };

  // Timer
  useEffect(() => {
    if (step === 'playing' && timeLeft !== null && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [step]);

  const startGame = () => {
    if (!studentName.trim() || !parentPhone.trim()) return;
    setStep('playing');
  };

  const handleAnswer = (option) => {
    if (showFeedback) return;
    setSelectedOption(option);
    const q = exam.questions[currentQ];
    const correct = option === q.options[q.options.indexOf(q.options.find(o => {
      // Compare with correct answer
      return false; // We don't have correct answer on frontend
    }))];
    // We don't know correct answer on frontend - just store answer
    setAnswers(prev => ({ ...prev, [q.id]: option }));
    setShowFeedback(true);
    setStars(prev => prev + 1); // Give star for attempting

    setTimeout(() => {
      setShowFeedback(false);
      setSelectedOption(null);
      if (currentQ < exam.questions.length - 1) {
        setCurrentQ(prev => prev + 1);
      } else {
        handleSubmit();
      }
    }, 1200);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    try {
      const res = await fetch(`${API_URL}/api/admission/exam/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_name: studentName,
          parent_phone: parentPhone,
          answers,
        })
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setStars(data.stars);
      }
    } catch (err) {
      console.error(err);
    }
    setStep('finished');
    setSubmitting(false);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const subjectColors = {
    language: { bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600', icon: '📖' },
    math: { bg: 'bg-green-500', light: 'bg-green-50', text: 'text-green-600', icon: '🔢' },
    logic: { bg: 'bg-purple-500', light: 'bg-purple-50', text: 'text-purple-600', icon: '🧩' },
    gk: { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-600', icon: '🌍' },
    creativity: { bg: 'bg-pink-500', light: 'bg-pink-50', text: 'text-pink-600', icon: '🎨' },
  };

  const optionColors = ['bg-blue-500 hover:bg-blue-600', 'bg-green-500 hover:bg-green-600', 'bg-purple-500 hover:bg-purple-600', 'bg-orange-500 hover:bg-orange-600'];

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-orange-50 to-pink-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-bounce text-6xl mb-4">🎮</div>
        <p className="text-gray-600 font-medium">Game load हो रहा है...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 shadow-xl text-center max-w-md">
        <div className="text-6xl mb-4">😕</div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Oops!</h2>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  // Welcome Screen
  if (step === 'welcome') return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-orange-50 to-pink-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full text-center" data-testid="exam-welcome">
        <div className="text-7xl mb-4 animate-bounce">🎮</div>
        <h1 className="text-2xl font-black text-gray-800 mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
          {exam?.welcome_message || 'चलो Game खेलते हैं!'}
        </h1>
        {exam?.school_name && (
          <p className="text-orange-500 font-semibold mb-4">{exam.school_name}</p>
        )}
        <p className="text-gray-500 mb-1">{exam?.class_name} — {exam?.total_questions} rounds</p>
        <p className="text-gray-400 text-sm mb-6">
          {exam?.time_limit_minutes} minutes
        </p>
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {exam?.questions?.map(q => q.subject).filter((v, i, a) => a.indexOf(v) === i).map(s => {
            const c = subjectColors[s] || subjectColors.gk;
            return (
              <span key={s} className={`px-3 py-1 ${c.light} ${c.text} rounded-full text-xs font-medium`}>
                {c.icon} {s}
              </span>
            );
          })}
        </div>
        <button
          onClick={() => setStep('info')}
          className="w-full py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-2xl font-bold text-lg shadow-lg shadow-orange-500/30 hover:scale-105 transition-transform"
          data-testid="exam-start-btn"
        >
          शुरू करें! 🚀
        </button>
      </div>
    </div>
  );

  // Student Info Screen
  if (step === 'info') return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-orange-50 to-pink-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full" data-testid="exam-info">
        <div className="text-center mb-6">
          <div className="text-5xl mb-2">👋</div>
          <h2 className="text-xl font-bold text-gray-800">पहले अपना नाम बताओ!</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">बच्चे का नाम</label>
            <input
              type="text"
              value={studentName}
              onChange={e => setStudentName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none text-lg"
              placeholder="जैसे: राहुल"
              data-testid="exam-student-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Parent का Phone Number</label>
            <input
              type="tel"
              value={parentPhone}
              onChange={e => setParentPhone(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none text-lg"
              placeholder="9876543210"
              data-testid="exam-parent-phone"
            />
          </div>
          <button
            onClick={startGame}
            disabled={!studentName.trim() || !parentPhone.trim()}
            className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl font-bold text-lg shadow-lg disabled:opacity-40 hover:scale-105 transition-transform"
            data-testid="exam-play-btn"
          >
            चलो खेलते हैं! 🎯
          </button>
        </div>
      </div>
    </div>
  );

  // Game / Question Screen
  if (step === 'playing' && exam?.questions) {
    const q = exam.questions[currentQ];
    const sc = subjectColors[q?.subject] || subjectColors.gk;
    const progress = ((currentQ + 1) / exam.questions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-orange-50 to-pink-100 flex flex-col">
        {/* Top Bar */}
        <div className="bg-white/80 backdrop-blur-md p-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-yellow-500 text-lg">⭐</span>
            <span className="font-bold text-gray-700">{stars}</span>
          </div>
          <div className="text-sm font-medium text-gray-500">
            Round {currentQ + 1}/{exam.questions.length}
          </div>
          {timeLeft !== null && (
            <div className={`font-mono font-bold ${timeLeft < 60 ? 'text-red-500 animate-pulse' : 'text-gray-600'}`}>
              ⏱ {formatTime(timeLeft)}
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-gray-200">
          <div className="h-full bg-gradient-to-r from-orange-400 to-pink-500 transition-all duration-500 rounded-r-full" style={{ width: `${progress}%` }} />
        </div>

        {/* Question Area */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-lg w-full" data-testid="exam-question">
            {/* Subject Badge */}
            <div className="text-center mb-4">
              <span className={`inline-flex items-center gap-1 px-4 py-1.5 ${sc.light} ${sc.text} rounded-full text-sm font-semibold`}>
                {sc.icon} {q.subject_label || q.subject}
              </span>
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-3xl p-6 shadow-xl mb-6">
              <p className="text-lg font-bold text-gray-800 text-center leading-relaxed" data-testid="question-text">
                {q.question}
              </p>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 gap-3">
              {q.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => handleAnswer(opt)}
                  disabled={showFeedback}
                  className={`w-full py-4 px-5 rounded-2xl text-white font-bold text-left text-lg transition-all ${
                    showFeedback && selectedOption === opt
                      ? 'scale-105 ring-4 ring-yellow-300'
                      : showFeedback
                        ? 'opacity-50'
                        : `${optionColors[i]} hover:scale-[1.02] active:scale-95`
                  } ${showFeedback && selectedOption === opt ? optionColors[i] : !showFeedback ? optionColors[i] : 'bg-gray-300'}`}
                  data-testid={`option-${i}`}
                >
                  <span className="mr-3 inline-block w-8 h-8 bg-white/20 rounded-lg text-center leading-8 text-sm">
                    {String.fromCharCode(65 + i)}
                  </span>
                  {opt}
                </button>
              ))}
            </div>

            {/* Feedback */}
            {showFeedback && (
              <div className="text-center mt-4 animate-bounce">
                <span className="text-3xl">⭐</span>
                <p className="text-gray-600 font-medium mt-1">अगला round आ रहा है...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Finished Screen
  if (step === 'finished') return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-green-50 to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full text-center" data-testid="exam-result">
        <div className="text-7xl mb-4">🎉</div>
        <h1 className="text-2xl font-black text-gray-800 mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
          वाह {studentName}! बहुत अच्छा किया!
        </h1>

        {/* Stars */}
        <div className="flex justify-center gap-1 my-4">
          {[1, 2, 3, 4, 5].map(s => (
            <span key={s} className={`text-4xl ${s <= (result?.stars || stars) ? '' : 'opacity-20'}`}>⭐</span>
          ))}
        </div>

        <p className="text-gray-500 mb-2">
          {result?.correct_count || 0} / {result?.total_questions || exam?.total_questions || 0} rounds जीते!
        </p>

        <p className="text-lg font-medium text-gray-700 mb-6">
          {result?.fun_message || '🌟 बहुत अच्छा प्रयास!'}
        </p>

        <div className="bg-green-50 rounded-2xl p-4 border border-green-100">
          <p className="text-green-700 text-sm">
            ✅ तुम्हारा result school को भेज दिया गया है। जल्दी ही तुम्हें बताया जाएगा!
          </p>
        </div>
      </div>
    </div>
  );

  return null;
}
